import React, { useState, useEffect, useRef } from 'react';
import { generateTokenForStore } from '~/api/skunkworks';
import { Link, useLocation } from '@remix-run/react';
import { useStore } from '~/context/store-context';
import { useFocusTrap } from '~/hooks/use-focus-trap';
import { useStores } from '~/hooks/useStores';

interface Store {
  name: string;
  value: string;
  address: string;
  hours: string;
  url: string;
  comingSoon?: boolean;
}

export default function AgeGate() {
  const location = useLocation();
  // Do not display the age gate on the thank-you page.
  if (location.pathname.includes('/thank-you')) return null;

  const { selectedStore, setSelectedStore } = useStore();
  const [isGateVisible, setIsGateVisible] = useState<boolean | null>(null);
  const [zipCode, setZipCode] = useState<string>('');
  // We'll store the current scrollY position here.
  const scrollYRef = useRef<number>(0);
  const [isAgeConfirmed, setIsAgeConfirmed] = useState(false);
  // Attach a ref to the scrollable container.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const modalTrapRef = useFocusTrap(Boolean(isGateVisible));

  // Stores come from the root loader (Store Central + website_locations merged,
  // or hardcoded fallback). Hour overrides are applied inside the adapter so
  // special dates (e.g., Kushmas, Thanksgiving) show the right hours here too.
  const baseStores = useStores();

  // Map to the simplified Store interface used in this component
  const stores: Store[] = baseStores.map((store) => ({
    name: store.name,
    value: store.value,
    address: store.address,
    url: store.url,
    hours: store.hours,
    comingSoon: store.comingSoon,
  }));

  // Clear coming soon store selection to prevent 500 errors
  useEffect(() => {
    const currentStore = stores.find((store) => store.value === selectedStore);
    if (currentStore?.comingSoon && selectedStore) {
      // Clear the problematic store selection
      localStorage.removeItem('selectedStore');
      document.cookie = 'selectedStore=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      setSelectedStore('');
      window.dispatchEvent(new Event('store:change'));
    }
  }, [selectedStore, setSelectedStore]);

  // On mount, check for the "ageApproved" cookie.
  useEffect(() => {
    const ageCookie = document.cookie.split('; ').find((row) => row.startsWith('ageApproved='));
    if (ageCookie) {
      setIsGateVisible(false);
    } else {
      setIsGateVisible(true);
    }
  }, []);

  // Lock background scroll by freezing the scroll position with scrollbar compensation.
  useEffect(() => {
    const preventTouchMove = (e: TouchEvent) => {
      if (scrollContainerRef.current && scrollContainerRef.current.contains(e.target as Node)) {
        return;
      }
      e.preventDefault();
    };

    if (isGateVisible) {
      // Save current scroll position.
      scrollYRef.current = window.scrollY;
      // Calculate scrollbar width to prevent layout shift
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      // Freeze the scroll by setting the body to fixed with scrollbar compensation.
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.paddingRight = `${scrollBarWidth}px`;
      // Also prevent scrolling on the root element
      document.documentElement.style.overflow = 'hidden';
      // Prevent touch scrolling on mobile (but allow within modal)
      document.addEventListener('touchmove', preventTouchMove, { passive: false });
      // Prevent zoom on double tap for modal overlays
      document.body.style.touchAction = 'manipulation';
    } else {
      document.removeEventListener('touchmove', preventTouchMove);
      // Restore all body styles.
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.paddingRight = '';
      document.body.style.touchAction = '';
      document.documentElement.style.overflow = '';
      // Restore the scroll position.
      window.scrollTo(0, scrollYRef.current);
    }

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.paddingRight = '';
      document.body.style.touchAction = '';
      document.documentElement.style.overflow = '';
      document.removeEventListener('touchmove', preventTouchMove);
    };
  }, [isGateVisible]);

  // When the gate becomes visible, scroll to the selected store if it's out of view.
  useEffect(() => {
    if (isGateVisible && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const el = container.querySelector<HTMLButtonElement>(
        `[data-store-value="${selectedStore}"]`,
      );
      if (el) {
        const elTop = el.offsetTop;
        const elBottom = elTop + el.offsetHeight;
        if (
          elTop < container.scrollTop ||
          elBottom > container.scrollTop + container.clientHeight
        ) {
          el.scrollIntoView({ block: 'center' });
        }
      }
    }
  }, [isGateVisible, selectedStore]);

  const handleZipLookup = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('Zip lookup for:', zipCode);
    // (Implement zip-to-store logic here)
  };

  const handleStoreSelect = (storeValue: string) => {
    // Don't allow selection of coming soon stores
    const store = stores.find((s) => s.value === storeValue);
    if (store?.comingSoon) {
      return;
    }
    setSelectedStore(storeValue);
  };

  const handleAgeConfirm = async () => {
    if (!selectedStore) return;
    const tokenKey = `token_${selectedStore}`;
    const existingToken = localStorage.getItem(tokenKey);
    if (!existingToken) {
      try {
        const token = await generateTokenForStore(selectedStore);
        if (token) {
          localStorage.setItem(tokenKey, token);
          document.cookie = `${tokenKey}=${encodeURIComponent(
            token,
          )}; path=/; max-age=${60 * 60 * 24 * 365}`;
        }
      } catch (error) {
        console.error('Error generating token for store:', error);
      }
    }
    localStorage.setItem('selectedStore', selectedStore);
    document.cookie = `selectedStore=${selectedStore}; path=/; max-age=${60 * 60 * 24 * 365}`;
    document.cookie = `ageApproved=true; path=/; max-age=${60 * 60 * 24 * 365}`;
    setIsGateVisible(false);
    window.dispatchEvent(new Event('store:change'));
  };

  if (isGateVisible === null || !isGateVisible) return null;
  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-label="Age confirmation"
    >
      <div
        ref={modalTrapRef}
        className="fixed top-4 left-1/2 z-[131] w-full max-w-[480px] -translate-x-1/2 transform bg-white shadow-md"
        style={{ display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr) auto', maxHeight: 'calc(100dvh - 2rem)' }}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="px-10 py-2 text-start text-lg font-bold text-black">Pick a Store</div>
        {/* Scrollable store list */}
        <div ref={scrollContainerRef} className="min-h-0 overflow-y-auto overscroll-contain px-4 py-4">
          <div className="grid gap-2 lg:gap-4">
            {stores.map((store) => (
              <button
                key={store.value}
                data-store-value={store.value}
                onClick={() => !store.comingSoon && handleStoreSelect(store.value)}
                disabled={store.comingSoon}
                className={`flex w-full justify-between p-4 px-6 text-black shadow-sm shadow-gray-400 transition ${
                  store.comingSoon
                    ? 'cursor-not-allowed bg-gray-100 opacity-60'
                    : selectedStore === store.value
                      ? 'bg-catalystdarkblue text-white'
                      : 'hover:bg-gray-100'
                }`}
              >
                <div className="text-start">
                  <p className="text-sm font-bold">{store.name.toUpperCase()}</p>
                  <p className="font-helvetica-neue text-xs">{store.hours}</p>
                </div>
                <div className="max-w-[15ch] text-right">
                  <p className="font-helvetica-neue text-xs">{store.address}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
        {/* Footer */}

        <div className="flex flex-col items-center justify-center space-y-3 border-t bg-white p-4">
          <label className="flex items-center space-x-2 text-sm text-black">
            <input
              type="checkbox"
              checked={isAgeConfirmed}
              onChange={(e) => setIsAgeConfirmed(e.target.checked)}
              className="accent-catalystblue h-4 w-4 focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
            />
            <span>I confirm I am 21 or older</span>
          </label>

          <button
            onClick={handleAgeConfirm}
            disabled={!isAgeConfirmed}
            className={`rounded px-6 py-2 transition focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:outline-none ${
              isAgeConfirmed
                ? 'bg-catalystdarkblue hover:bg-catalystblue text-white'
                : 'cursor-not-allowed bg-gray-300 text-gray-600'
            }`}
          >
            Confirm
          </button>

          <p className="text-center text-xs lg:text-sm">
            By clicking above you agree <br />
            to our{' '}
            <Link
              className="underline focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:outline-none"
              to="/privacy-policy"
            >
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link
              className="underline focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:outline-none"
              to="/terms-of-use"
            >
              Terms of use
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
